import React from 'react';
import { Card } from './Card';
import { AccessibilityIcon } from './icons/AccessibilityIcon';

interface AccessibilityCheckerProps {
  onAnalyze: () => void;
  isAnalyzing: boolean;
  auditResult?: any | null;
  onOpenResults?: () => void;
}

export const AccessibilityChecker: React.FC<AccessibilityCheckerProps> = ({
  onAnalyze,
  isAnalyzing,
  auditResult,
  onOpenResults
}) => {
  const [dotCount, setDotCount] = React.useState(1);
  const [dotTimer, setDotTimer] = React.useState<NodeJS.Timeout | null>(null);

  // Dots animation effect
  React.useEffect(() => {
    if (isAnalyzing && !dotTimer) {
      setDotCount(1);
      
      const timer = setInterval(() => {
        setDotCount(prev => {
          return prev >= 4 ? 1 : prev + 1;
        });
      }, 200);
      
      setDotTimer(timer);
    } else if (!isAnalyzing && dotTimer) {
      clearInterval(dotTimer);
      setDotTimer(null);
      setDotCount(1);
    }
    
    return () => {
      if (dotTimer) {
        clearInterval(dotTimer);
        setDotTimer(null);
      }
    };
  }, [isAnalyzing, dotTimer]);

  return (
    <div className="cy:p-0.5 cy:rounded-lg cy:bg-gradient-to-r cy:from-blue-500 cy:to-purple-600">
      <Card className="cy:border-0 cy:bg-blue-50 cy:rounded-lg">
        <div className="cy:p-4">
        <h2 
          className="cy:m-0 cy:mb-2 cy:text-gray-900" 
          style={{ fontSize: '18px', fontWeight: 600, lineHeight: '100%' }}
        >
          Accessibility checker
        </h2>
        <p 
          className="cy:m-0 cy:mb-4 cy:text-gray-600" 
          style={{fontSize: '14px', fontWeight: 400, lineHeight: '140%' }}
        >
          Get instant insights into accessibility issues.
        </p>
        {auditResult ? (
          <div
            role="button"
            onClick={onOpenResults}
            className="cy:w-full cy:py-3 cy:px-3 cy:rounded-lg cy:border cy:border-blue-500 cy:bg-white cy:hover:bg-blue-50 cy:cursor-pointer cy:flex cy:items-center cy:justify-between"
            aria-label="Open accessibility results"
          >
            <div className="cy:flex cy:items-center cy:gap-2">
              <AccessibilityIcon size={18} className="cy:text-blue-600" />
              <span className="cy:text-gray-900" style={{ fontSize: '16px', fontWeight: 600 }}>Results are ready</span>
            </div>
            <svg className="cy:w-4 cy:h-4 cy:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        ) : (
          <button
            onClick={onAnalyze}
            disabled={isAnalyzing}
            className="cy:w-full cy:bg-blue-600 cy:hover:bg-blue-700 cy:disabled:bg-blue-600 cy:disabled:cursor-not-allowed cy:text-white cy:py-3 cy:px-3 cy:rounded-lg cy:border-1 cy:border-blue-500 cy:cursor-pointer cy:transition-colors cy:flex cy:items-center cy:justify-center cy:gap-2"
            style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              lineHeight: '100%'
            }}
          >
            <AccessibilityIcon size={18} className="cy:flex-shrink-0" />
            {isAnalyzing ? (
              <span className="cy:flex cy:items-center">
                <span>Analyzing page</span>
                <span className="cy:inline-block cy:w-5 cy:text-left">
                  {'.'.repeat(dotCount)}
                </span>
              </span>
            ) : (
              'Audit this page'
            )}
          </button>
        )}
        </div>
      </Card>
    </div>
  );
};

